import { appliedHealthCareInflation } from '../appliedHealthCareInflation';
import { CarePhase } from '../appModel';
import { careSettingFromEnvironments } from './careSettings';

export const WEEKS_PER_MONTH = 52 / 12;

export interface CareScenarioPhase {
  carePhase: CarePhase;
  months: number;
  careEnvironment: CareEnvironment;
  inHome: boolean;
  careHoursPerWeek: number;
  familyHoursPerWeek: number;
  cost: number;
}

export interface CareScenarioSummary {
  currentAge: number | null;
  careStartAge: number;
  yearsUntilCare: number;
  totalMonths: number;
  phases: CareScenarioPhase[];
  totalCost: number;
  careHoursPerWeek: number;
  familyHoursPerWeek: number;
  familySavings: number;
  inflationPercent: number;
  careSetting: ConsumerCareSetting | null;
}

const perWeek = (hours: number, months: number) => (months > 0 ? hours / (months * WEEKS_PER_MONTH) : 0);

/** The client's care plan in the terms the consumer flow talks about. */
export function summarizeCareScenario(client: Client): CareScenarioSummary {
  const phases = (client.phaseCosts ?? [])
    .filter(phase => phase.phaseCareMonthsNeeded > 0)
    .map(phase => ({
      carePhase: phase.carePhase,
      months: phase.phaseCareMonthsNeeded,
      careEnvironment: phase.appliedCareEnvironment,
      inHome: phase.inHomeEnvironment,
      careHoursPerWeek: perWeek(phase.phaseCareHoursNeeded, phase.phaseCareMonthsNeeded),
      familyHoursPerWeek: perWeek(phase.phaseFamilyCareHoursProvided, phase.phaseCareMonthsNeeded),
      cost: phase.phaseInflatedTotalCost,
    }));

  const totalMonths = phases.reduce((sum, phase) => sum + phase.months, 0);
  const totalHours = (key: 'phaseCareHoursNeeded' | 'phaseFamilyCareHoursProvided') =>
    (client.phaseCosts ?? []).reduce((sum, phase) => sum + phase[key], 0);

  return {
    currentAge: client.clientDemographics?.age ?? null,
    careStartAge: client.appliedInferenceSet?.ltcAtAge ?? 0,
    yearsUntilCare: Math.max(0, client.appliedInferenceSet?.yearsTillLtc ?? 0),
    totalMonths,
    phases,
    totalCost: client.allPhaseCosts?.allPhaseInflatedTotalCost ?? 0,
    careHoursPerWeek: perWeek(totalHours('phaseCareHoursNeeded'), totalMonths),
    familyHoursPerWeek: perWeek(totalHours('phaseFamilyCareHoursProvided'), totalMonths),
    familySavings: client.allPhaseCosts?.allPhaseInflatedFamilyCareCost ?? 0,
    inflationPercent: appliedHealthCareInflation(client, true),
    careSetting: careSettingFromEnvironments(client.appliedCareEnvironments ?? {}),
  };
}

export function durationInWords(months: number): string {
  const rounded = Math.round(months);
  const years = Math.floor(rounded / 12);
  const remainder = rounded % 12;
  const part = (count: number, unit: string) => `${count} ${unit}${count === 1 ? '' : 's'}`;

  return [years ? part(years, 'year') : '', remainder ? part(remainder, 'month') : '']
    .filter(Boolean)
    .join(' and ') || part(0, 'month');
}
